// pms-adapters / supabase / support — the error boundary.
//
// Supabase/PostgREST return { data, error }; PL/pgSQL RPCs RAISE (surfaced as an
// error with the French message our RPCs write). This is where that becomes a
// pms-core Result, so nothing throws across the port seam (A4). At P1 we map
// coarsely — the retryable-vs-dead-letter refinement the JobRunner needs is P2.

import { err, ok, type PmsError, type PropertyCode, type QueryScope, type Result } from '@lemontaigu/pms-core';
import type { PostgrestError } from '@supabase/supabase-js';

export type { SupabaseClient } from '@supabase/supabase-js';

/** PostgREST/RPC error → PmsError. Transport-level codes are retryable. */
export function toPmsError(e: PostgrestError): PmsError {
  // 08xxx = connection exception, 57xxx = operator-intervention → retryable.
  const retryable = /^(08|57)/.test(e.code ?? '');
  return {
    kind: retryable ? 'transient' : 'unknown',
    message: e.message,
    retryable,
    cause: e,
  };
}

/** Wrap a PostgREST result whose data is the value. */
export function fromResult<T>(r: { data: T | null; error: PostgrestError | null }): Result<T> {
  if (r.error) return { ok: false, error: toPmsError(r.error) };
  if (r.data === null) return err('not_found', 'Ressource introuvable');
  return ok(r.data);
}

/** Wrap a result where null data is legitimately "no error, nothing to return". */
export function fromVoid(r: { error: PostgrestError | null }): Result<void> {
  if (r.error) return { ok: false, error: toPmsError(r.error) };
  return ok(undefined);
}

/** Wrap a list result (null → []). */
export function fromList<Row, T>(
  r: { data: Row[] | null; error: PostgrestError | null },
  map: (row: Row) => T,
): Result<T[]> {
  if (r.error) return { ok: false, error: toPmsError(r.error) };
  return ok((r.data ?? []).map(map));
}

// ── Scope binding (P1.5 step 6) ──────────────────────────────────────────────
//
// Two one-liners, applied at every table read and every RPC call. They look
// trivial and they are the entire service-role backstop: under the service-role
// key RLS does not run, so `.from('pms_reservation').select()` returns EVERY
// établissement's rows unless something narrows it. That something is here.
//
// A browser-scoped repository adds nothing, deliberately — RLS has already
// narrowed the read and the RPCs resolve the property from the actor. Adding a
// filter there would be belt-and-braces on a gate that works; omitting it here
// would be the tenancy leak itself.

/** Narrow a PostgREST query to the bound établissement. No-op for `actor`. */
export function scopedQuery<Q>(query: Q, scope: QueryScope, column = 'property_code'): Q {
  // The constraint is asserted rather than declared as `Q extends { eq(): Q }`:
  // PostgREST's builder is generic over row, result and relationship types, and
  // a self-referential constraint over it makes tsc give up with "type
  // instantiation is excessively deep". The cast is confined to these two
  // helpers, and the call sites keep their exact builder types.
  if (scope.kind !== 'property') return query;
  return (query as unknown as { eq(c: string, v: unknown): Q }).eq(column, scope.propertyCode);
}

/** Narrow to the bound TENANT — for `pms_guest`, the one table scoped by
 *  operator rather than by établissement. */
export function scopedTenantQuery<Q>(query: Q, scope: QueryScope): Q {
  if (scope.kind !== 'property') return query;
  return (query as unknown as { eq(c: string, v: unknown): Q }).eq('tenant_code', scope.tenantCode);
}

/** The scope columns to spread into a DIRECT table insert — the few writes that
 *  do not go through an RPC (`pms_room_block`, `pms_rate`). Empty under `actor`,
 *  where the column default and RLS still cover it. */
export function scopedInsert(scope: QueryScope): Record<string, string> {
  return scope.kind === 'property' ? { property_code: scope.propertyCode } : {};
}

/** The `p_property_code` argument for an RPC: the bound property, or null to
 *  let the database resolve it from the actor (which it refuses when
 *  ambiguous — `pms_resolve_scope`). */
export function scopedRpcProperty(scope: QueryScope): PropertyCode | null {
  return scope.kind === 'property' ? scope.propertyCode : null;
}
